import {
  AtlasMenuContentItem,
  AtlasMenuItemProps,
  Button,
  DeprecatedLink,
  Menu,
} from "@resource/atlas";
import { useCallback, useMemo } from "react";
import { useEventCallback } from "react-hooks/useEventCallback";

const DEFAULT_ITEM_PROPS: Partial<AtlasMenuItemProps> = {
  isSelectable: true,
  size: "compact",
};

type FakeMultiSelectProps = {
  label: string;
  items: AtlasMenuContentItem[];
  selectedIds: string[];
  onSelectedIdsChange: (value: string[]) => void;
  minWidth?: number | string;
};

function findItemByValue(value: string, items: AtlasMenuContentItem[]) {
  return items.find(
    (item): item is AtlasMenuContentItem & { _type: "item" } => {
      // eslint-disable-next-line no-underscore-dangle
      if (item._type === "item" && (item.value ?? item.children) === value)
        return true;
      return false;
    }
  );
}

type TriggerLabelData = {
  left: string;
  right?: string;
  info?: string;
};

function computeTriggerInfo({
  items,
  selectedIds,
}: Pick<FakeMultiSelectProps, "selectedIds" | "items">) {
  return selectedIds
    .map((id) => findItemByValue(id, items)?.children)
    .filter(Boolean)
    .join(", ");
}

function computeTriggerLabel({
  label,
  items,
  selectedIds,
}: Pick<
  FakeMultiSelectProps,
  "label" | "selectedIds" | "items"
>): TriggerLabelData {
  if (selectedIds.length === 0) return { left: label };
  const firstItemName =
    findItemByValue(selectedIds[0], items)?.children ?? label;
  if (selectedIds.length === 1)
    return { left: firstItemName, info: firstItemName };
  return {
    left: firstItemName,
    right: `, +${selectedIds.length - 1}`,
    info: computeTriggerInfo({ items, selectedIds }),
  };
}

export default function FakeMultiSelect({
  label,
  items: itemsProp,
  selectedIds,
  onSelectedIdsChange,
  minWidth,
}: FakeMultiSelectProps) {
  const triggerLabelData = useMemo(
    () => computeTriggerLabel({ label, items: itemsProp, selectedIds }),
    [itemsProp, label, selectedIds]
  );

  const onSelectedIdsChangeMemo = useEventCallback(onSelectedIdsChange);

  const onSelectHandler = useCallback(
    (value: string) =>
      onSelectedIdsChangeMemo(
        selectedIds.includes(value) // if value is already selected
          ? selectedIds.filter((v) => v !== value) // remove it
          : [...selectedIds, value] // else, add it
      ),
    [onSelectedIdsChangeMemo, selectedIds]
  );

  const items = useMemo(() => {
    const output: AtlasMenuContentItem[] = [];

    itemsProp.forEach((item) => {
      // eslint-disable-next-line no-underscore-dangle
      if (item._type === "item") {
        const value = item.value ?? item.children;
        const isSelected = selectedIds.includes(value);
        output.push({
          ...item,
          _type: "item",
          title: item.children,
          isSelected,
          onClick: (e) => {
            e.preventDefault();
            onSelectHandler(value);
          },
          ...DEFAULT_ITEM_PROPS,
        });
      }
    });

    return output;
  }, [itemsProp, onSelectHandler, selectedIds]);

  return (
    <Menu.Root searchable>
      <Menu.Trigger>
        <Button
          isActive={selectedIds.length > 0}
          title={triggerLabelData.info}
          className="max-w-[9rem]"
        >
          <p className="flex">
            <span className="truncate">{triggerLabelData.left}</span>
            {triggerLabelData.right && <span>{triggerLabelData.right}</span>}
          </p>
        </Button>
      </Menu.Trigger>
      <Menu.Content
        footer={
          selectedIds.length > 0 && (
            <div className="px-3 py-2 flex">
              <p className="text-body-md flex-grow">
                <b>{selectedIds.length}</b> selected
              </p>
              <DeprecatedLink onPress={() => onSelectedIdsChange([])}>
                Clear
              </DeprecatedLink>
            </div>
          )
        }
        style={{ minWidth }}
        isVirtual
        items={items}
      />
    </Menu.Root>
  );
}
